import React, { useCallback, useEffect, useState } from "react";
import { useT } from "@transifex/react";
import { useHistory, useLocation } from "react-router-dom";
import { Typography } from "storybook/components/typography";
import { Notification } from "storybook/components/notification";
import { Button } from "storybook/components/button";
import { WaitingGraphic } from "./waiting-graphic";
import { useFeatureFlags } from "context/admin-content";
import { useRiskReport } from "context/risk-report";
import { Loading } from "components/loading";
import {
  Headings,
  RiskList,
  RiskSection,
  WaitingWrapper,
  RiskListItemIncreased,
  RiskListItemDecreased,
  RiskSectionContainer,
  OpeningTextContainer,
  SubHeadingContainer,
  MainHeadingContainer,
} from "./styles";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { parseRiskReportData } from "./parse-nodes";

type Props = {
  stacked?: boolean;
  error: boolean;
  handleReviewClick?: () => void;
  isWaiting?: boolean;
};

export function RiskSummary({
  error,
  stacked = false,
  handleReviewClick = () => null,
  isWaiting = false,
}: Props) {
  const t = useT();
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const { disablePatientApprovalForRemoteScreenings } = useFeatureFlags();

  const isHomePage = location.pathname === "/";

  const {
    reportHtml,
    refetchRiskReport,
    isLoading: reportLoading,
    hasUpdatePendingApproval,
    setHasUpdatePendingApproval,
  } = useRiskReport();

  const handleTryAgain = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      refetchRiskReport();
    },
    [refetchRiskReport]
  );

  useEffect(() => {
    const fetchPendingApprovalStatus = async () => {
      setLoading(true);
      const patientId = localStorage.getItem("ciq_id");
      const idToken = localStorage.getItem("id_token");

      try {
        const response = await fetch(
          `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/risk_profiles/status`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
          }
        );
        const { has_update_pending_approval } = await response.json();
        setHasUpdatePendingApproval(has_update_pending_approval);
      } catch (error) {
        console.error("Error fetching risk profile status", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingApprovalStatus();
  }, [setHasUpdatePendingApproval]);

  const handleExploreOptions = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("explore-options", "true");
    history.push(`/complete?${searchParams.toString()}`);
  };

  if (loading || reportLoading) {
    return <Loading />;
  }

  if (!disablePatientApprovalForRemoteScreenings && hasUpdatePendingApproval) {
    return (
      <WaitingWrapper>
        <WaitingGraphic />
        <Typography theme="default" variant="body1" color="ciqBrand">
          Your results are awaiting approval
        </Typography>
      </WaitingWrapper>
    );
  }

  const {
    status,
    heading,
    subHeading,
    openingText,
    promptText,
    increasing,
    decreasing,
  } = reportHtml
    ? parseRiskReportData(reportHtml)
    : {
        status: null,
        heading: null,
        subHeading: null,
        openingText: null,
        promptText: null,
        increasing: [],
        decreasing: [],
      };

  if (error) {
    return (
      <Notification
        fullWidth
        theme="default"
        variant="warning"
        title={t("Error loading risk assessment")}
      >
        <Typography theme="default" variant="body1" color="black">
          <a href="/" role="button" onClick={handleTryAgain}>
            {t("Please try again")}
          </a>
        </Typography>
      </Notification>
    );
  }

  const riskHeadingColor = status === "increased" ? "monza" : "apple";

  return (
    <div className="risk-summary">
      <Headings className="notranslate">
        <MainHeadingContainer data-id="MainHeadingContainer">
          <Typography
            theme="default"
            variant="h5"
            component="h2"
            color="ciqBrandDark"
          >
            {heading}
          </Typography>
        </MainHeadingContainer>
        <SubHeadingContainer>
          <Typography
            theme="default"
            variant="h5"
            component="h3"
            color={riskHeadingColor}
          >
            <b>{subHeading}</b>
          </Typography>

          {isHomePage && (
            <>
              <Button
                theme="patient-facing"
                variant="outlined"
                color="secondary"
                onClick={handleReviewClick}
                disabled={isWaiting}
              >
                {t("Change My Survey Responses")}
              </Button>
              <Button
                theme="patient-facing"
                variant="contained"
                color="default"
                disabled={isWaiting}
                onClick={handleExploreOptions}
              >
                {t("Review My Next Steps")}
              </Button>
            </>
          )}
        </SubHeadingContainer>
      </Headings>
      <OpeningTextContainer className="notranslate">
        <Typography theme="default" variant="body1" color="black">
          {openingText}
        </Typography>
      </OpeningTextContainer>
      <div className="notranslate">
        <Typography theme="default" variant="body1" color="black">
          <strong>{promptText}</strong>
        </Typography>
      </div>
      <RiskSectionContainer $stacked={stacked}>
        <RiskSection>
          <Typography
            theme="default"
            variant="body1"
            color="ciqBrandDark"
            component="div"
          >
            <div>
              Factors <b>increasing</b> your cancer risk
            </div>
          </Typography>
          {increasing.length > 0 ? (
            <RiskList className="notranslate">
              {increasing.map((item, idx: number) => (
                <RiskListItemIncreased key={idx}>
                  {typeof item === "string" ? item : item.text}
                </RiskListItemIncreased>
              ))}
            </RiskList>
          ) : (
            <p>N/A</p>
          )}
        </RiskSection>
        <RiskSection>
          <Typography
            theme="default"
            variant="body1"
            color="ciqBrandDark"
            component="div"
          >
            <div>
              Factors <b>decreasing</b> your cancer risk
            </div>
          </Typography>
          {decreasing.length > 0 ? (
            <RiskList className="notranslate">
              {decreasing.map((item, idx: number) => (
                <RiskListItemDecreased key={idx}>
                  {typeof item === "string" ? item : item.text}
                </RiskListItemDecreased>
              ))}
            </RiskList>
          ) : (
            <p>N/A</p>
          )}
        </RiskSection>
      </RiskSectionContainer>
    </div>
  );
}
